import type { FC } from 'react';

import { Button, Icon, Stack, Tag, Text, tv } from '@eluve/components';
import type { TagProps } from '@eluve/components';

import { FindingEvidence } from './FindingEvidence';
import type { Decision, PendingFinding } from './types';

const rowStyles = tv({
  base: 'w-full rounded-lg border p-3.5 shadow-sm transition-all',
  variants: {
    decision: {
      approved: 'border-border-positive-subtle bg-bg-positive-subtle',
      edited: 'border-border-insights-subtle bg-bg-insights-subtle',
      rejected:
        'border-border-negative-subtle bg-bg-negative-subtle opacity-55',
      pending: 'border-border-secondary bg-bg-primary',
    },
  },
});

const DECISION_TAG: Record<
  'approved' | 'edited' | 'rejected',
  { label: string; color: TagProps['color'] }
> = {
  approved: { label: 'Approved', color: 'positive' },
  edited: { label: 'Edited', color: 'insights' },
  rejected: { label: 'Rejected', color: 'negative' },
};

export const FindingRow: FC<{
  finding: PendingFinding;
  disabled?: boolean;
  onApprove: () => void;
  onReject: () => void;
  onUndo: () => void;
  onEdit: () => void;
}> = ({ finding, disabled, onApprove, onReject, onUndo, onEdit }) => {
  const decided: Decision = finding.decision;
  const clinicalDetails = [
    finding.detail,
    finding.measureCode?.code ? `Code ${finding.measureCode.code}` : null,
    finding.targetDate ? `Target date ${finding.targetDate}` : null,
  ].filter((detail): detail is string => Boolean(detail));

  return (
    <div className={rowStyles({ decision: decided })}>
      <Stack
        direction="horizontal"
        align="center"
        justify="between"
        gap={3}
        wrap
      >
        <Text weight="medium" className="min-w-0 flex-1">
          {finding.display}
        </Text>

        <Stack
          direction="horizontal"
          align="center"
          gap={1}
          fullWidth={false}
          className="shrink-0"
        >
          {decided !== 'pending' ? (
            <>
              <Tag
                size="s"
                subtle={false}
                color={DECISION_TAG[decided].color}
                label={DECISION_TAG[decided].label}
                className="h-[30px] rounded-lg px-3"
              />
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={onUndo}
              >
                <Icon name="Undo2" size="xs" />
                Undo
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                className="border-border-positive-subtle bg-bg-positive-subtle text-text-positive hover:bg-bg-positive-subtle"
                onClick={onApprove}
              >
                <Icon name="Check" size="xs" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={onEdit}
              >
                <Icon name="Pencil" size="xs" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                className="border-border-negative-subtle bg-bg-negative-subtle text-text-negative hover:bg-bg-negative-subtle"
                onClick={onReject}
              >
                <Icon name="X" size="xs" />
                Reject
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {clinicalDetails.length > 0 ? (
        <Text size="xs" color="tertiary" className="mt-1">
          {clinicalDetails.join(' · ')}
        </Text>
      ) : null}

      <FindingEvidence evidence={finding.evidence} />
    </div>
  );
};
